import { useState } from "react";
import { format } from "date-fns";
import { useUserSettingsData } from "@/shared/hooks/useUserSettingsData";
import { useRevokeTPA } from "@/shared/hooks/useRevokeTPA";
import { useReactivateTPA } from "@/shared/hooks/useReactivateTPA";
import { InsightsCard, InsightsDividerList, InsightsDividerRow } from "./InsightsCard";
import { Button } from "@/shared/components/ui/button";
import { RevokeAuthorizationDialog } from "./RevokeAuthorizationDialog";
import { enqueueSnackbar } from "notistack";

import type { UserDocumentWithId } from "@/shared/types/documentTypes";

interface TPAAuthorizationInsightsProps {
  signatureType: "tpa" | "irs";
  document: UserDocumentWithId;
}

function getSignatureData(
  tpaSignatures: Record<string, unknown> | null,
  signatureType: string,
): Record<string, unknown> | null {
  if (!tpaSignatures) return null;
  const entry = tpaSignatures[signatureType];
  if (!entry || typeof entry !== "object") return null;
  return entry as Record<string, unknown>;
}

function extractSignerFromDisplayName(displayName: string | null | undefined): string | null {
  if (!displayName) return null;
  const parts = displayName.split(" - ");
  return parts.length > 1 ? parts.slice(1).join(" - ").trim() : null;
}

export default function TPAAuthorizationInsights({
  signatureType,
  document,
}: TPAAuthorizationInsightsProps) {
  const { tpaSignatures, fullName, isLoading, supabaseData } = useUserSettingsData();
  const revokeMutation = useRevokeTPA();
  const reactivateMutation = useReactivateTPA();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const signatureData = getSignatureData(tpaSignatures, signatureType);
  const documentSignerName = extractSignerFromDisplayName(document.display_name);
  const uploadedByUserId = (document as unknown as { uploaded_by_user_id?: number })
    .uploaded_by_user_id;
  const isOwnDocument =
    documentSignerName?.toLowerCase() === fullName?.toLowerCase() ||
    (uploadedByUserId != null && uploadedByUserId === supabaseData?.user_id);

  if (isLoading) return null;

  if (!isOwnDocument || !signatureData) {
    return (
      <div className="space-y-4 p-4">
        <InsightsCard title="Document details" noPadding>
          <InsightsDividerList>
            {documentSignerName ? (
              <InsightsDividerRow label="Signed by" value={documentSignerName} />
            ) : null}
            <InsightsDividerRow label="Method" value="Web-based digital signature" />
          </InsightsDividerList>
        </InsightsCard>
      </div>
    );
  }

  const isRevoked = signatureData.revoked === true;
  const revokedAt = typeof signatureData.revokedAt === "string" ? signatureData.revokedAt : null;
  const signedAt = typeof signatureData.timestamp === "string" ? signatureData.timestamp : null;

  const handleRevoke = async (reason: string) => {
    try {
      await revokeMutation.mutateAsync({ signatureType, reason: reason || undefined });
      setIsConfirmOpen(false);
      enqueueSnackbar("Authorization revoked", { variant: "success" });
    } catch {
      enqueueSnackbar("Couldn't revoke authorization. Please try again.", { variant: "error" });
    }
  };

  return (
    <>
      <div className="space-y-4 p-4">
        <InsightsCard title="Authorization" noPadding>
          <div className="p-4 space-y-3">
            {isRevoked ? (
              <>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-stone-400 shrink-0" />
                  <p className="text-sm font-semibold">
                    You revoked on {revokedAt ? format(new Date(revokedAt), "MMM d, yyyy") : ""}
                  </p>
                </div>
                <p className="text-sm text-secondary">
                  Re-authorize Elayne anytime if you'd like us to reach out to institutions or
                  submit paperwork for you again.
                </p>
                <Button
                  variant="brand"
                  disabled={reactivateMutation.isPending}
                  onClick={async () => {
                    try {
                      await reactivateMutation.mutateAsync({ signatureType });
                      enqueueSnackbar("Authorization reactivated", { variant: "success" });
                    } catch {
                      enqueueSnackbar("Couldn't reactivate authorization. Please try again.", { variant: "error" });
                    }
                  }}
                >
                  {reactivateMutation.isPending ? "Reactivating..." : "Re-sign authorization"}
                </Button>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2">
                  <span className="h-2 w-2 rounded-full bg-emerald-500 shrink-0" />
                  <p className="text-sm font-semibold">
                    Active since {signedAt ? format(new Date(signedAt), "MMMM d, yyyy") : ""}
                  </p>
                </div>
                <p className="text-sm text-secondary">
                  Elayne is authorized to contact institutions and submit forms on your behalf.
                </p>
                <button
                  type="button"
                  className="text-sm text-brand font-medium hover:underline"
                  onClick={() => setIsConfirmOpen(true)}
                >
                  Manage authorization
                </button>
              </>
            )}
          </div>
        </InsightsCard>

        <InsightsCard title="Document details" noPadding>
          <InsightsDividerList>
            <InsightsDividerRow
              label="Signature date and time"
              value={signedAt ? format(new Date(signedAt), "MMMM d, yyyy 'at' h:mm a") : ""}
            />
            <InsightsDividerRow label="Signed by" value={fullName} />
            <InsightsDividerRow label="Method" value="Web-based digital signature" />
          </InsightsDividerList>
        </InsightsCard>
      </div>

      <RevokeAuthorizationDialog
        open={isConfirmOpen}
        onOpenChange={setIsConfirmOpen}
        onRevoke={handleRevoke}
        isRevoking={revokeMutation.isPending}
        signatureType={signatureType}
      />
    </>
  );
}
